import { nextTick, onActivated, onDeactivated, onMounted, onUnmounted } from 'vue'
import { gsap } from 'gsap'

const revealTargets = [
  '.section-copy',
  '.library-feature',
  '.library-grid li',
  '.workflow-step',
  '.scenario-list li',
  '.stat-item',
  '.pricing-info',
  '.faq-item',
  '.footer-cta-card',
  '.footer-brand',
  '.footer-column',
  '.footer-bottom',
  '.tools-section'
].join(', ')

const parseStatValue = (value) => {
  const raw = String(value)
  const match = raw.match(/^([\d,.]+)(.*)$/)

  if (!match) {
    return null
  }

  const numericText = match[1].replace(/,/g, '')
  const target = Number(numericText)

  if (!Number.isFinite(target)) {
    return null
  }

  return {
    target,
    decimals: numericText.includes('.') ? numericText.split('.')[1].length : 0,
    suffix: match[2]
  }
}

const animateStatValue = (element) => {
  const parsed = parseStatValue(element.textContent.trim())

  if (!parsed) {
    return null
  }

  const counter = { value: 0 }

  return gsap.to(counter, {
    value: parsed.target,
    duration: 1.4,
    ease: 'power2.out',
    onUpdate: () => {
      element.textContent = `${counter.value.toFixed(parsed.decimals)}${parsed.suffix}`
    }
  })
}

export function useHomeMotion(homePage) {
  let motionContext
  let activeTweens = []
  let activeObservers = []

  const trackTween = (tween) => {
    if (tween) {
      activeTweens.push(tween)
    }

    return tween
  }

  const trackObserver = (observer) => {
    if (observer) {
      activeObservers.push(observer)
    }

    return observer
  }

  const observeRevealTargets = (isMobile) => {
    const elements = Array.from(homePage.value.querySelectorAll(revealTargets))

    if (!elements.length) {
      return
    }

    gsap.set(elements, {
      autoAlpha: 0,
      y: isMobile ? 18 : 38
    })

    if (!('IntersectionObserver' in window)) {
      trackTween(gsap.to(elements, {
        autoAlpha: 1,
        y: 0,
        duration: isMobile ? 0.58 : 0.68,
        ease: 'power3.out',
        stagger: 0.06,
        overwrite: 'auto'
      }))
      return
    }

    const observer = trackObserver(new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        const element = entry.target
        const index = Number(element.dataset.homeRevealIndex || 0)
        observer.unobserve(element)

        trackTween(gsap.to(element, {
          autoAlpha: 1,
          y: 0,
          duration: isMobile ? 0.58 : 0.68,
          delay: Math.min((index % 4) * 0.035, 0.12),
          ease: 'power3.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility'
        }))
      })
    }, {
      root: null,
      threshold: 0.12,
      rootMargin: isMobile ? '0px 0px -12% 0px' : '0px 0px -10% 0px'
    }))

    elements.forEach((element, index) => {
      element.dataset.homeRevealIndex = String(index)
      observer.observe(element)
    })
  }

  const observeStats = () => {
    const statsSection = homePage.value.querySelector('.stats-section')

    if (!statsSection) {
      return
    }

    const runCounters = () => {
      gsap.utils.toArray('.stat-value', homePage.value).forEach((element) => {
        trackTween(animateStatValue(element))
      })
    }

    if (!('IntersectionObserver' in window)) {
      runCounters()
      return
    }

    const observer = trackObserver(new IntersectionObserver((entries) => {
      const shouldRun = entries.some((entry) => entry.isIntersecting)

      if (!shouldRun) {
        return
      }

      observer.disconnect()
      runCounters()
    }, {
      root: null,
      threshold: 0.2,
      rootMargin: '0px 0px -22% 0px'
    }))

    observer.observe(statsSection)
  }

  const setupHomeMotion = () => {
    if (!homePage.value || motionContext) {
      return
    }

    motionContext = gsap.matchMedia()

    motionContext.add(
      {
        reduceMotion: '(prefers-reduced-motion: reduce)',
        isDesktop: '(min-width: 641px)',
        isMobile: '(max-width: 640px)'
      },
      (context) => {
        const { reduceMotion, isMobile } = context.conditions

        if (reduceMotion) {
          const reducedMotionTargets = homePage.value.querySelectorAll(
            `.hero-shot, .sound-wave, .sound-wave i, .caption-rail, ${revealTargets}`
          )
          if (reducedMotionTargets.length) {
            gsap.set(reducedMotionTargets, { clearProps: 'all' })
          }
          return
        }

        const yOffset = isMobile ? 14 : 38
        const intro = gsap.timeline({
          defaults: {
            duration: isMobile ? 0.56 : 0.68,
            ease: 'power3.out'
          }
        })

        // 用 fromTo 写明终点：from() 会把元素当前状态当终点，重复初始化时若元素仍处于隐藏态会永久隐藏
        const shown = { autoAlpha: 1, x: 0, y: 0, clearProps: 'transform,opacity,visibility' }
        intro
          .fromTo('.hero-kicker', { autoAlpha: 0, x: isMobile ? 0 : -14, y: yOffset }, shown)
          .fromTo('.main-heading', { autoAlpha: 0, x: isMobile ? 0 : -18, y: yOffset }, shown, '-=0.42')
          .fromTo('.home-subtitle', { autoAlpha: 0, y: yOffset * 0.7 }, shown, '-=0.34')
          .fromTo('.hero-description', { autoAlpha: 0, y: yOffset * 0.7 }, shown, '-=0.34')
          .fromTo('.hero-content .action-buttons .btn', { autoAlpha: 0, y: 12 }, { ...shown, stagger: 0.08 }, '-=0.3')
          .fromTo('.hero-shot', { autoAlpha: 0, y: isMobile ? 16 : 24 }, {
            ...shown,
            duration: isMobile ? 0.72 : 0.9
          }, '-=0.64')

        observeRevealTargets(isMobile)
        observeStats()
      },
      homePage.value
    )
  }

  const teardownHomeMotion = () => {
    activeObservers.forEach((observer) => observer.disconnect())
    activeObservers = []
    motionContext?.revert()
    activeTweens.forEach((tween) => tween.kill())
    activeTweens = []
    motionContext = null
  }

  const restartHomeMotion = async () => {
    await nextTick()
    setupHomeMotion()
  }

  onMounted(restartHomeMotion)
  onActivated(restartHomeMotion)
  onDeactivated(teardownHomeMotion)
  onUnmounted(teardownHomeMotion)
}
